import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Mail, Building2, Phone, Clock, Search, Download,
  ChevronDown, MessageSquare, X, Save, ArrowRight, Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestLabels = {
  general: "Demo general", erp: "Sistema ERP", crm: "CRM",
  inventory: "Inventarios", billing: "Facturación", custom: "Desarrollo a medida",
};
const interestColors = {
  general: "#94a3b8", erp: "#3b82f6", crm: "#06b6d4",
  inventory: "#8b5cf6", billing: "#10b981", custom: "#f59e0b",
};

const STATUSES = [
  { id: "nuevo", label: "Nuevo", color: "#3b82f6", bg: "#3b82f620" },
  { id: "contactado", label: "Contactado", color: "#f59e0b", bg: "#f59e0b20" },
  { id: "negociacion", label: "En Negociación", color: "#8b5cf6", bg: "#8b5cf620" },
  { id: "convertido", label: "Convertido", color: "#10b981", bg: "#10b98120" },
  { id: "perdido", label: "Perdido", color: "#ef4444", bg: "#ef444420" },
];

const getStatus = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];

const StatusBadge = ({ status, onClick }) => {
  const s = getStatus(status);
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 font-medium transition-all hover:opacity-80"
      style={{ backgroundColor: s.bg, color: s.color }}
      data-testid={`status-badge-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
      {s.label}
      <ChevronDown className="w-3 h-3" />
    </button>
  );
};

const StatusDropdown = ({ currentStatus, onSelect, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -5, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -5, scale: 0.95 }}
    className="absolute z-50 top-8 left-0 bg-[#0b1221] border border-white/10 shadow-xl py-1 min-w-[180px]"
    data-testid="status-dropdown"
  >
    {STATUSES.map((s) => (
      <button
        key={s.id}
        onClick={() => { onSelect(s.id); onClose(); }}
        data-testid={`status-option-${s.id}`}
        className={`w-full text-left px-4 py-2 flex items-center gap-2 text-sm hover:bg-white/5 transition-colors ${
          currentStatus === s.id ? "bg-white/5" : ""
        }`}
      >
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
        <span style={{ color: s.color }}>{s.label}</span>
        {currentStatus === s.id && <span className="ml-auto text-[#94a3b8] text-xs">actual</span>}
      </button>
    ))}
  </motion.div>
);

const NotesModal = ({ lead, onSave, onClose }) => {
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(lead.id, notes);
    setSaving(false);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-[#0b1221] border border-white/10 w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="notes-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Notas - {lead.name}</h3>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="notes-textarea"
          className="w-full bg-[#02040a] border border-white/10 p-3 text-white text-sm min-h-[150px] focus:border-[#3b82f6] focus:outline-none resize-none placeholder:text-[#94a3b8]/50"
          placeholder="Agrega notas sobre este prospecto... (ej: Llamada programada para viernes, interesado en módulo de inventarios)"
        />
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="text-[#94a3b8] text-sm px-4 py-2 hover:text-white transition-colors">Cancelar</button>
          <motion.button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#3b82f6] text-white text-sm font-medium px-5 py-2 flex items-center gap-2"
            whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.3)" }}
            data-testid="notes-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar Notas"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const PipelineBar = ({ leads }) => {
  const counts = STATUSES.reduce((acc, s) => {
    acc[s.id] = leads.filter((l) => (l.status || "nuevo") === s.id).length;
    return acc;
  }, {});
  const total = leads.length || 1;

  return (
    <div className="glass-card p-5 mb-6" data-testid="pipeline-bar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold text-sm">Pipeline de Ventas</h3>
        <span className="text-[#94a3b8] text-xs">{leads.length} leads totales</span>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden mb-3 bg-white/5">
        {STATUSES.map((s) => {
          const pct = (counts[s.id] / total) * 100;
          if (pct === 0) return null;
          return (
            <motion.div
              key={s.id}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              style={{ backgroundColor: s.color }}
              className="h-full"
              title={`${s.label}: ${counts[s.id]}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4">
        {STATUSES.map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
            <span className="text-[#94a3b8]">{s.label}</span>
            <span className="text-white font-semibold">{counts[s.id]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LeadsView = ({ auth }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [notesLead, setNotesLead] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/leads`, {
        auth: { username: auth.username, password: auth.password },
      });
      setLeads(response.data?.items || response.data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [auth.username, auth.password]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, { status: newStatus }, {
        auth: { username: auth.username, password: auth.password },
      });
      setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, status: newStatus } : l));
      const s = getStatus(newStatus);
      toast.success(`Lead actualizado a "${s.label}"`);
    } catch {
      toast.error("Error al actualizar el lead");
    }
  };

  const saveNotes = async (leadId, notes) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, { notes }, {
        auth: { username: auth.username, password: auth.password },
      });
      setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, notes } : l));
      toast.success("Notas guardadas");
    } catch {
      toast.error("Error al guardar las notas");
    }
  };

  const exportCSV = () => {
    const headers = ["Nombre", "Email", "Empresa", "Teléfono", "Interés", "Estado", "Notas", "Fuente", "Fecha"];
    const rows = leads.map((l) => [
      l.name, l.email, l.company || "", l.phone || "",
      interestLabels[l.interest] || l.interest || "", getStatus(l.status).label,
      (l.notes || "").replace(/"/g, "'"), l.source || "",
      l.created_at ? new Date(l.created_at).toLocaleString("es") : ""
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const filtered = leads.filter((l) => {
    const matchSearch = l.name?.toLowerCase().includes(search.toLowerCase()) ||
      l.email?.toLowerCase().includes(search.toLowerCase()) ||
      l.company?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || (l.status || "nuevo") === statusFilter;
    return matchSearch && matchStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="leads-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads del Demo</h1>
          <p className="text-[#94a3b8] text-sm">{leads.length} prospecto{leads.length !== 1 ? "s" : ""} capturado{leads.length !== 1 ? "s" : ""}</p>
        </div>
        {leads.length > 0 && (
          <motion.button
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-[#3b82f6]/10 text-[#3b82f6] text-sm font-medium border border-[#3b82f6]/30 hover:bg-[#3b82f6]/20 transition-colors"
            whileHover={{ scale: 1.02 }}
            data-testid="export-leads-csv"
          >
            <Download className="w-4 h-4" /> Exportar CSV
          </motion.button>
        )}
      </div>

      {/* Pipeline visualization */}
      {leads.length > 0 && <PipelineBar leads={leads} />}

      {/* Filters */}
      {leads.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email o empresa..."
              data-testid="leads-search"
              className="w-full bg-[#0b1221] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-[#94a3b8]/50 focus:border-[#3b82f6] focus:outline-none transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#94a3b8]" />
            <div className="flex gap-1">
              <button
                onClick={() => setStatusFilter("all")}
                data-testid="filter-all"
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${
                  statusFilter === "all" ? "bg-white/10 text-white" : "text-[#94a3b8] hover:text-white"
                }`}
              >
                Todos
              </button>
              {STATUSES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStatusFilter(s.id)}
                  data-testid={`filter-${s.id}`}
                  className={`text-xs px-3 py-1.5 font-medium transition-colors ${
                    statusFilter === s.id ? "text-white" : "text-[#94a3b8] hover:text-white"
                  }`}
                  style={statusFilter === s.id ? { backgroundColor: s.bg, color: s.color } : {}}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Leads list */}
      {filtered.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Users className="w-12 h-12 text-[#3b82f6]/30 mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">
            {leads.length === 0 ? "Sin leads aún" : "Sin resultados"}
          </h3>
          <p className="text-[#94a3b8] text-sm">
            {leads.length === 0
              ? "Los leads se capturarán cuando los visitantes completen el tour del demo."
              : "Intenta con otro filtro o término de búsqueda."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead, index) => {
            const leadStatus = lead.status || "nuevo";
            return (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="glass-card p-5 hover:border-[#3b82f6]/30 transition-all"
                data-testid={`lead-item-${lead.id}`}
                style={{ borderLeftWidth: 3, borderLeftColor: getStatus(leadStatus).color }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className="w-11 h-11 flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${getStatus(leadStatus).color}, ${getStatus(leadStatus).color}80)` }}
                    >
                      {lead.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h3 className="text-white font-semibold">{lead.name}</h3>
                        <span
                          className="text-xs px-2 py-0.5 font-medium"
                          style={{
                            backgroundColor: `${interestColors[lead.interest] || "#94a3b8"}20`,
                            color: interestColors[lead.interest] || "#94a3b8",
                          }}
                        >
                          {interestLabels[lead.interest] || "General"}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#94a3b8]">
                        <span className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-[#3b82f6]" />
                          <a href={`mailto:${lead.email}`} className="hover:text-[#3b82f6] transition-colors">{lead.email}</a>
                        </span>
                        {lead.company && (
                          <span className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-[#06b6d4]" />
                            {lead.company}
                          </span>
                        )}
                        {lead.phone && (
                          <span className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-[#8b5cf6]" />
                            {lead.phone}
                          </span>
                        )}
                      </div>

                      {/* Notes preview */}
                      {lead.notes && (
                        <p className="text-[#94a3b8] text-xs mt-2 bg-white/5 px-3 py-1.5 border-l-2 border-[#3b82f6]/30 line-clamp-1">
                          {lead.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="text-[#94a3b8] text-xs">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString("es", { day: "numeric", month: "short" }) : ""}
                    </span>

                    {/* Status selector */}
                    <div className="relative">
                      <StatusBadge
                        status={leadStatus}
                        onClick={() => setOpenDropdownId(openDropdownId === lead.id ? null : lead.id)}
                      />
                      <AnimatePresence>
                        {openDropdownId === lead.id && (
                          <StatusDropdown
                            currentStatus={leadStatus}
                            onSelect={(s) => updateLeadStatus(lead.id, s)}
                            onClose={() => setOpenDropdownId(null)}
                          />
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Notes button */}
                    <button
                      onClick={() => setNotesLead(lead)}
                      data-testid={`notes-btn-${lead.id}`}
                      className="flex items-center gap-1 text-[#94a3b8] text-xs hover:text-[#3b82f6] transition-colors"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      {lead.notes ? "Editar notas" : "Agregar notas"}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Notes modal */}
      <AnimatePresence>
        {notesLead && (
          <NotesModal
            lead={notesLead}
            onSave={saveNotes}
            onClose={() => setNotesLead(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LeadsView;
